import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  AbstractControl,
} from '@angular/forms';
import { PasswordModule } from 'primeng/password';
import { MatIconModule } from '@angular/material/icon';

/**
 * PasswordInput Component
 * Password input field with show/hide toggle functionality
 * Uses PrimeNG Password component for styling and responsiveness
 * Supports reactive forms and template-driven forms
 */
@Component({
  selector: 'app-password-input',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PasswordModule, MatIconModule],
  templateUrl: './password-input.html',
  styleUrl: './password-input.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordInput),
      multi: true,
    },
  ],
})
export class PasswordInput implements ControlValueAccessor {
  @Input() id: string = 'password';
  @Input() label: string = 'Password';
  @Input() placeholder: string = 'Enter Password';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorMessage: string = '';
  @Input() showError: boolean | null | undefined = false;
  @Input() formControl?: AbstractControl | null;

  // Custom styling
  @Input() labelClass: string = 'block text-gray-700 text-sm font-medium';
  @Input() inputClass: string =
    'w-full h-10 px-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-botmaaOrange pr-10';
  @Input() errorClass: string = 'text-red-600 text-xs';
  @Input() containerClass: string = 'relative';
  @Input() toggleButtonClass: string =
    'absolute top-3/4 right-2 pb-2 -translate-y-1/2 flex items-center text-gray-500 hover:text-gray-700';

  // Responsive classes
  @Input() responsiveLabelClass: string = '';
  @Input() responsiveInputClass: string = '';

  showPassword: boolean = false;
  value: string = '';
  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
    this.onTouched();
  }

  onFormControlInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.formControl) {
      this.formControl.setValue(target.value);
      this.formControl.markAsTouched();
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  getLabelClasses(): string {
    return [this.labelClass, this.responsiveLabelClass].filter(Boolean).join(' ');
  }

  getInputClasses(): string {
    return [this.inputClass, this.responsiveInputClass].filter(Boolean).join(' ');
  }
}
